import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useReactTable, flexRender, getCoreRowModel, SortingState, getSortedRowModel } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Row } from 'components/Row';
import { RowBetween } from 'components/Row';
import { TYPE } from 'theme';
import Column from 'components/Column';
import Select from 'components/Select';
import { isMobile } from 'react-device-detect';
import { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled as muiStyled } from '@mui/material/styles';
import { ReactComponent as ArrowRightSVG } from 'assets/images/partners/arrow-right.svg';
import { ReactComponent as QuestionSVG } from 'assets/images/dashboard/question.svg';
import { ReactComponent as DoubleXSVG } from 'assets/images/home/assets/doubleX.svg';
import useCapsuleMethods from 'hooks/useCapsuleMethods';
import { ASSETS_LIST_JSON_API } from 'constant';
import { CardStyles, GradientTextColor2 } from 'theme/commonStyles';
import { GoldGradientBorderBox, GradientBorderBox } from 'components/styled/Box';
import AssetIcon from 'components/Icons/Assets';
import DAppIcon from 'components/Icons/DApps';
import Tooltip from 'components/TooltipInfo';
import ExpandableSection from 'components/ExpandableSection';

const Wrapper = styled.div`
    ${CardStyles}
    color: #fff;
    padding: 45px;
    border-radius: 22px;
    transform: translateX(5px);
    backdrop-filter: blur(3px);
    margin-top: 30px;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        padding: 20px;
        margin-left: -10px;
        margin-right: -10px;
    `};

    position: relative;
    .coming-soon-img {
        width: 218px;
        height: 304px;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        ${({ theme }) => theme.mediaWidth.upToSmall`
            width: 109px;
            height: 157px;
        `};
    }
`;

const TableContainer = styled.div`
    overflow: auto;
    max-height: 800px;
    position: relative;

    /* 隐藏滚动条但保持滚动功能 */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    &::-webkit-scrollbar {
        /* Chrome, Safari and Opera */
        display: none;
    }

    ${({ theme }) => theme.mediaWidth.upToSmall`
        height: 520px;
    `};
`;

const Table = styled.table`
    display: grid;
`;

const THead = styled.thead`
    display: grid;
    position: sticky;
    top: 0;
    z-index: 1;
    background: #333;
    border-radius: 12px;
`;

const TBody = styled.tbody`
    display: grid;
    position: relative;
`;

const TR = styled.tr`
    display: flex;
    width: 100%;
    border-radius: 12px;
    &:not(.thead-tr):hover {
        background: linear-gradient(
            90deg,
            rgba(106, 13, 173, 0.5) 0%,
            rgba(247, 147, 26, 0.12) 55%,
            rgba(247, 147, 26, 0) 100%
        );
    }
`;

const TH = styled.th`
    font-size: 18px;
    color: #fff;
    font-weight: 400;
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        height: 48px;
        font-size: 12px;
    `};
`;

const TD = styled.td`
    box-sizing: border-box;
    display: flex;
    height: 72px;
    padding: 0 16px;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        height: 48px;
    `};
`;

const Tag = styled(GradientBorderBox)`
    color: #fff;
    padding: 0px 10px 2px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 400;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        font-size: 12px;
    `};
`;

const TVL = styled(GradientBorderBox).attrs({ borderWidth: 3 })`
    color: #fff;
    padding: 16px 20px;
    border-radius: 30px;
    font-weight: 400;
    filter: drop-shadow(0px 10px 20px rgba(54, 103, 123, 0.2));
    ${({ theme }) => theme.mediaWidth.upToSmall`
        flex: 1;
        padding: 10px;
        border-radius: 15px;
    `};
`;

const Tip = styled.div`
    width: 16px;
    height: 16px;
    background-color: #ccc;
    color: #fff;
    border-radius: 10px;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;

    > svg {
        &:not(:first-child) {
            margin-left: -16px; // 负margin实现重叠效果
        }
    }
`;

const Action = styled.a`
    width: 38px;
    height: 28px;
    background-color: ${({ theme }) => theme.c.primary01};
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        width: 28px;
        height: 20px;
    `};
`;

const StyledSelect = styled(Select)`
    filter: drop-shadow(0px 10px 20px rgba(54, 103, 123, 0.1));
    width: 150px;
    height: 48px !important;
    border-radius: 8px !important;
    font-size: 16px;
    color: #fff;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        width: 140px;
        height: 40px;
        border-radius: 10px;
        font-size: 12px;
    `};

    /* &.MuiInputBase-root {

        &:hover, &:active {
            border: 1px solid #fff;
        }
    } */

    &.MuiInputBase-root .MuiSelect-select {
        font-size: 14px;
    }
`;

const GradientText = styled(TYPE.desc)`
    ${GradientTextColor2}
    display: inline-block;
`;

const TransparentTooltip = muiStyled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: 'rgba(0, 0, 0, 0)',
        padding: 0,
    },
    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
        marginTop: '8px',
    },
}));

const isProd = !/localhost|dev/.test(window.location.hostname);

const ExpandButton = styled.button<{ isExpanded: boolean }>`
    width: 100%;
    height: 40px;
    background: transparent;
    border: none;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-bottom: 16px;
    gap: 8px;

    svg {
        transition: transform 0.3s ease;
        transform: ${({ isExpanded }) => (isExpanded ? 'rotate(180deg)' : 'rotate(0)')};
    }

    &:hover {
        opacity: 0.8;
    }
`;

export default function AssetsList() {
    const [type, setType] = useState('');
    const [asset, setAsset] = useState('');
    const [platform, setPlatform] = useState('');
    const [sort, setSort] = useState('');
    const [assetsLists, setAssetsLists] = useState<any>(null);
    useEffect(() => {
        fetch(ASSETS_LIST_JSON_API)
            .then((res) => res.json())
            .then((data) => {
                setAssetsLists(data);
            });
    }, []);
    const [records, platformOptions, platformRateUnit] = useMemo(() => {
        if (!assetsLists) {
            return [[], [], {}];
        }

        const records: any[] = [];
        const platformOptions: any[] = [];
        const platformRateUnit: any = {};
        assetsLists.platforms.forEach((platform: any) => {
            platformOptions.push({
                text: platform.name,
                value: platform.name,
            });
            const { assetBaseRate, rateUnit } =
                assetsLists.assetBaseRates.find((item: any) => item.platforms.includes(platform.name)) ?? {};
            platformRateUnit[platform.name] = rateUnit;
            platform.assets.forEach((assetObj: any) => {
                records.push({
                    ...platform,
                    platform: platform.name,
                    ...assetObj, // { asset, testLink, prodLink }
                    baseRate: assetBaseRate?.[assetObj.asset] ?? '-',
                    rateUnit,
                });
            });
        });

        return [records, platformOptions, platformRateUnit];
    }, [assetsLists]);

    const filterRecords = useMemo(
        () => (platform ? records.filter((record) => record.platform === platform) : records),
        [records, platform],
    );

    // const { isMinted } = useCapsuleMethods();
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [sorting, setSorting] = useState<SortingState>([]);
    // const [isExpanded, setIsExpanded] = useState(false);

    const columns = [
        {
            header: 'Assets',
            accessorKey: 'asset',
            size: isMobile ? 200 : 320,
            cell: (props: any) => {
                const assetValue = props.getValue();
                const assets = assetValue.split('/').filter(Boolean);
                return (
                    <Row gap="8px">
                        <IconWrapper>
                            {assets.map((each: string) => (
                                <AssetIcon key={each} asset={each} />
                            ))}
                            <AssetIcon asset={asset} />
                        </IconWrapper>
                        <TYPE.desc color="#fff">{assetValue}</TYPE.desc>
                    </Row>
                );
            },
        },
        {
            header: 'Type',
            accessorKey: 'type',
            size: isMobile ? 80 : 180,
            cell: (props: any) => {
                return (
                    <Row gap="8px" justify="center">
                        <TYPE.desc color="#fff">{props.getValue()}</TYPE.desc>
                    </Row>
                );
            },
        },
        {
            header: 'Platform',
            accessorKey: 'platform',
            size: isMobile ? 180 : 300,
            cell: (props: any) => {
                const doubleX = props.row.original.doubleX;
                return doubleX && props.getValue() !== 'Stakestone' ? (
                    <Row justify="center">
                        <TransparentTooltip
                            title={<GoldGradientBorderBox>2X points</GoldGradientBorderBox>}
                            placement="bottom"
                        >
                            <Row gap="8px" justify="center">
                                <DAppIcon dAppName={props.getValue()} />
                                <GradientText>{props.getValue()}</GradientText>
                                <DoubleXSVG width={isMobile ? '20px' : '27px'} />
                            </Row>
                        </TransparentTooltip>
                    </Row>
                ) : (
                    <Row gap="8px" justify="center">
                        <DAppIcon dAppName={props.getValue()} />
                        <TYPE.desc color="#fff">{props.getValue()}</TYPE.desc>
                    </Row>
                );
            },
        },
        {
            header: () => (
                <Row gap="8px" justify="center">
                    <span>Est. APY</span>
                    <Tooltip title="This is the estimated average APY. Staking assets in a Stake-type protocol will earn yield returns. The APY is updated based on the partner's update time.">
                        <QuestionSVG width={16} height={16} style={{ transform: 'translateY(2px)' }} />
                    </Tooltip>
                    {/* <AutoColumn gap="2px">
                        <SortAscSVG style={{ color: '#fff' }} />
                        <SortDescSVG style={{ color: '#fff' }} />
                    </AutoColumn> */}
                </Row>
            ),
            accessorKey: 'apy',
            size: isMobile ? 80 : 180,
            cell: (props: any) => {
                const apy = props.getValue();
                return (
                    <Row gap="8px" justify="center" align="center">
                        <TYPE.desc color="#fff">{apy}</TYPE.desc>
                    </Row>
                );
            },
            enableSorting: true,
        },
        // {
        //     header: () => (
        //         <>
        //             <Row gap="8px" justify="center">
        //                 <span>GEC Rewards</span>
        //                 <Tooltip title="GEC rewards are earned through all interactions with dApps within the GOAT Network ecosystem.">
        //                     <QuestionSVG width={16} height={16} style={{ transform: 'translateY(2px)' }} />
        //                 </Tooltip>
        //             </Row>
        //             {platform && platformRateUnit[platform] && (
        //                 <Row sx={{ fontSize: 12, fontWeight: 400 }} justify="center">
        //                     {platformRateUnit[platform]}
        //                 </Row>
        //             )}
        //         </>
        //     ),
        //     accessorKey: 'baseRate',
        //     size: isMobile ? 120 : 200,
        //     cell: (props: any) => {
        //         return (
        //             <Row gap="8px" justify="center">
        //                 <TYPE.desc color="#fff">{props.getValue()}</TYPE.desc>
        //                 {!!isMinted && (
        //                     <Tooltip title="Minting Capsule NFT will provide a 1.1x points acceleration.">
        //                         <Tag>1.1x</Tag>
        //                     </Tooltip>
        //                 )}
        //             </Row>
        //         );
        //     },
        // },
        // {
        //     header: () => (
        //         <Row gap="8px">
        //             <span>GOAT Points</span>
        //             <Tooltip title="GOAT Points are earned by participating in projects within the GOAT Network ecosystem and are related to the project partners and asset types.">
        //                 <QuestionSVG width={16} height={16} style={{ transform: 'translateY(2px)' }} />
        //             </Tooltip>
        //         </Row>
        //     ),
        //     accessorKey: 'points',
        //     size: isMobile ? 100 : 170,
        //     cell: (props: any) => {
        //         return (
        //             <Row gap="8px" justify="center">
        //                 {props.getValue() ? <Tag>{props.getValue()}</Tag> : '-'}
        //             </Row>
        //         );
        //     },
        // },
        {
            header: '',
            accessorKey: isProd ? 'prodLink' : 'testLink',
            size: isMobile ? 80 : 130,
            cell: (props: any) => {
                return (
                    <Row gap="8px" justify="center">
                        <Action href={props.getValue()} target="_blank">
                            <ArrowRightSVG width={8} height={8} />
                        </Action>
                    </Row>
                );
            },
        },
    ];

    const table = useReactTable({
        columns,
        data: filterRecords,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        state: {
            sorting,
        },
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: (updater) => {
            setSorting(updater);
            if (!!table.getRowModel().rows.length) {
                rowVirtualizer.scrollToIndex?.(0);
            }
        },
    });

    const rows = table.getRowModel().rows;

    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 72,
        overscan: 5,
        measureElement:
            typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
                ? (element) => element?.getBoundingClientRect().height
                : undefined,
    });

    return (
        <ExpandableSection defaultExpanded={false}>
            <Wrapper>
                <RowBetween
                    marginBottom={isMobile ? '20px' : '36px'}
                    style={{
                        alignItems: isMobile ? 'flex-start' : 'center',
                    }}
                >
                    <Column>
                        <div style={{ position: 'relative' }}>
                            <TYPE.title fontSize={24} mobileFz={18}>
                                Assets Lists
                            </TYPE.title>
                        </div>
                        <TYPE.desc
                            fontSize="14px"
                            fontWeight="400"
                            mobileFz={12}
                            color="#fff"
                            style={{
                                maxWidth: isMobile ? '170px' : 'unset',
                            }}
                        >
                            Explore and participate in dApps to gain more GOAT Energy CUBE(GEC)
                        </TYPE.desc>
                    </Column>
                </RowBetween>
                <Row gap="16px" marginBottom={isMobile ? '20px' : '36px'}>
                    <StyledSelect
                        value={platform}
                        onChange={setPlatform}
                        placeholder="Platform"
                        items={platformOptions}
                        disablePlaceholder={false}
                    />
                </Row>

                <TableContainer ref={tableContainerRef}>
                    <Table>
                        <THead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TR key={headerGroup.id} className="thead-tr">
                                    {headerGroup.headers.map((header) => (
                                        <TH
                                            key={header.id}
                                            style={{
                                                width: header.getSize(),
                                            }}
                                        >
                                            <div
                                                {...{
                                                    className: header.column.getCanSort()
                                                        ? 'cursor-pointer select-none'
                                                        : '',
                                                    onClick: header.column.getToggleSortingHandler(),
                                                }}
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                            </div>
                                        </TH>
                                    ))}
                                </TR>
                            ))}
                        </THead>
                        <TBody
                            style={{
                                height: `${rowVirtualizer.getTotalSize()}px`,
                            }}
                        >
                            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                const row = rows[virtualRow.index];

                                return (
                                    <TR
                                        data-index={virtualRow.index}
                                        ref={(node) => rowVirtualizer.measureElement(node)}
                                        key={row.id}
                                        style={{
                                            position: 'absolute',
                                            transform: `translateY(${virtualRow.start}px)`,
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <TD
                                                    key={cell.id}
                                                    style={{
                                                        width: cell.column.getSize(),
                                                    }}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TD>
                                            );
                                        })}
                                    </TR>
                                );
                            })}
                        </TBody>
                    </Table>
                </TableContainer>
            </Wrapper>
        </ExpandableSection>
    );
}
